import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ILead } from "../../models/Lead";
import { IVehicle, VehicleStatus, YardStatus } from "../../models/Vehicle";
import formatDateTime from "../../utils/formatDateTime";
import useStockNumber from "../../hooks/useStockNumber";
import useLocations from "../../data/useLocations";
import VehicleStatusCell from "./VehicleStatusCell";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

export default function VehicleHeaderBar({ vehicle, lead }: { vehicle: IVehicle; lead?: ILead | null }) {
  const { t } = useTranslation("vehicles");
  const { getStockNumber } = useStockNumber();
  const { locations } = useLocations();
  const isTme = CheckFeatureFlag("TME");

  const yardStatusRaw = vehicle.yardStatus !== YardStatus.None ? YardStatus[vehicle.yardStatus] : null;
  const yardStatus = yardStatusRaw ? t(yardStatusRaw) : "—";

  const daysOnSite = (() => {
    if (!vehicle.collectedOn) return "—";
    const days = Math.max(0, Math.floor(DateTime.now().diff(DateTime.fromISO(vehicle.collectedOn), "days").days));
    return days === 1 ? t("{{n}} day", { n: days }) : t("{{n}} days", { n: days });
  })();

  const arrived = vehicle.collectedOn ? formatDateTime(vehicle.collectedOn, DateTime.DATE_SHORT) : "—";
  const location =
    vehicle.yardLocation?.name ??
    locations.data?.data?.find((loc) => loc.id === vehicle.locationId)?.name ??
    "—";
  const source = lead?.source ?? "—";
  const reference = getStockNumber(vehicle) || vehicle.stockNumber || vehicle.vrm || "—";

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-medium text-gray-900">
          {`${vehicle.vrm ?? ""} ${vehicle.combinedMake ?? ""} ${vehicle.modelRangeDescription ?? ""}`.trim()}
        </h1>
        <div className="w-fit">
          <VehicleStatusCell status={vehicle.status} statusDate={vehicle.statusDate} />
        </div>
      </div>

      <div className={`mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 ${isTme ? "lg:grid-cols-6" : "lg:grid-cols-7"}`}>
        <Stat label={t("Current Status")} value={t(VehicleStatus[vehicle.status])} />
        {!isTme && <Stat label={t("Yard Status")} value={yardStatus} />}
        <Stat label={t("Days on Site")} value={daysOnSite} />
        <Stat label={t("Location")} value={location} />
        <Stat label={t("Arrived")} value={arrived} />
        <Stat label={t("Source")} value={String(source)} />
        <Stat label={t("Reference")} value={reference} />
      </div>
    </div>
  );
}
